<template>
  <div>
    <!-- Gráfico con histórico -->
    <div>
      <chart v-if="showChart" :chartOptions="chartOptions" :monthWindow="6" :dynamicXAxis="false" :xAxisDayInterval="7"
        :applyGradient="true" chartType="areaspline"
        :legendOptions="{ enabled: false }"
        :chartBackgroundColor="chartBackgroundColor" :chartTitle="titleChart"
        :titleOptions="{ align: 'center', style: { color: titleChartColor } }" :markersEnabled="true"
        :markerRadius="4" />
        <p v-else class="text-center text-muted">
      {{ $t('not-enough-data-chart') }}
    </p>
    </div>
  </div>
</template>

<script>
import Chart from '@/components/base/Chart.vue'
import typeMood from '@/constants/typeMood'
import { BACKGROUND_COLOUR, BLUE_COLOUR, GREEN_COLOUR, RED_COLOUR, YELLOW_COLOUR, DARK_YELLOW_COLOUR, GRAY_COLOUR } from '@/constants/colours'
import { BAlert, BCard } from 'bootstrap-vue'

function getEmojiSrc (fileName) {
  const ROOT_RELATIVE_PATH = '/static-appopensalud/img/icons/emojis/'
  return ROOT_RELATIVE_PATH + fileName
}
const tickIcons = {
  1: { src: getEmojiSrc('emoji-very-sad.svg'), color: RED_COLOUR },
  2: { src: getEmojiSrc('emoji-no-feelings.svg'), color: DARK_YELLOW_COLOUR },
  3: { src: getEmojiSrc('emoji-happy.svg'), color: GREEN_COLOUR }
}
const SIX_MONTHS_MS = 183 * 24 * 60 * 60 * 1000
const SIX_MONTH_POINTS = 24
const PX_PER_POINT = 40

export default {
  name: 'MoodTracker',
  components: { Chart, BAlert, BCard },
   props: {
    // Recibimos el array de registros de mood desde el padre
    moodData: {
      type: Array,
      required: true,
      // Cada elemento esperara tener: { date: 'YYYY-MM-DD', name: string, typeMood: number }
      default: () => []
    }
  },
  data () {
    return {
      colorText: GRAY_COLOUR,
      selectedMood: null,
      chartBackgroundColor: BACKGROUND_COLOUR,
      positive: [],
      negative: [],
      neutral: [],
      moodRecords: [],
      // 1) Definimos chartData como objeto vacío,
      // y lo iremos rellenando en loadMoods()
      chartData: {
        variableData: [],
        moodsData: []
      },

      // 2) Definimos un ancho mínimo por defecto para el gráfico
      minWidthChart: 600,

      titleChart: this.$t('mood-title-chart'),
      titleChartColor: BLUE_COLOUR,
      showMoodSelectorModal: false,
      showChart: false,

      chartOptions: {
        xAxis: {
          type: 'datetime',
          minPadding: 0.03,
          maxPadding: 0.03,
          startOnTick: true,
          endOnTick: true,
          tickInterval: 7 * 24 * 60 * 60 * 1000
        },
        yAxis: {
          title: { text: this.$t('mood') },
          labels: {
            formatter () {
              const icon = tickIcons[this.value]
              if (!icon) return this.value
              return `<span
                        style="
                          display:inline-block;
                          width:22px;height:22px;
                          background-color:${icon.color};
                          -webkit-mask:url('${icon.src}') center/contain no-repeat;
                          mask:          url('${icon.src}') center/contain no-repeat;
                        ">
                      </span>`
            },
            useHTML: true,
            style: { fontSize: '22px', lineHeight: '1' }
          },
          allowDecimals: false,
          gridLineColor: BLUE_COLOUR,
          gridLineWidth: 0.25
        },
        credits: false,
        tooltip: {
          outside: false,
          useHTML: true,
          formatter () {
            const dateStr = new Date(this.x).toLocaleDateString()
            const colorMarker = `<span style="color:${this.point.color}">●</span>`
            return `
              ${colorMarker} <strong>${this.series.name}</strong><br/>
              ${dateStr} - ${this.point.moodName || 'Sin nombre'}
            `
          }
        },
        series: [
          {
            name: this.$t('mood'),
            data: [],
            lineColor: BACKGROUND_COLOUR
          }
        ]
      }
    }
  },
  computed: {
    // Ahora que chartData y minWidthChart existen, scrollableOpts no dará error
    scrollableOpts () {
    // nº de puntos que realmente pintas en la serie principal
    const pts = (this.chartOptions.series[0].data || []).length

    // Si caben en una pantalla, nada de scroll
    if (pts <= SIX_MONTH_POINTS) return null
    const minWidth = Math.max(this.minWidthChart, pts * PX_PER_POINT)

    // scrollPositionX:1 ⇒ arranca pegado a la derecha (últimos datos)
    return { minWidth, scrollPositionX: 1 }
  }
  },
  watch: {
    // Cada vez que cambie el prop moodData, refrescamos la gráfica
    moodData: {
      handler (newArr) {
        this.moodRecords = newArr
        this.chartData.moodsData = newArr
        this.updateChartData()
        this.moodRecords = newArr
       this.chartData.moodsData = newArr
       this.updateChartData()
       /* 2️⃣  Solo usamos el filtro para decidir si
              mostramos la gráfica o el <p> “no hay datos”   */
       const sixBack = Date.now() - SIX_MONTHS_MS
       const recentPoints = newArr.filter(r =>
         new Date(r.date).getTime() >= sixBack
       )
       this.showChart = recentPoints.length >= 2
      },
      immediate: true
    }
  },
  methods: {
    updateChartData () {
      // Mapeamos moodRecords a puntos
      const dataPoints = this.moodRecords.map(record => ({
        x: new Date(record.date).getTime(),
        y: record.typeMood,
        moodName: record.name
      }))

      // Asignamos a la serie principal
      this.chartOptions.series[0].data = dataPoints.map(point => {
        // Coloreamos cada punto según su value
        if (point.y === typeMood.POSITIVE) {
          point.color = GREEN_COLOUR
        } else if (point.y === typeMood.NEUTRAL) {
          point.color = YELLOW_COLOUR
        } else {
          point.color = RED_COLOUR
        }
        return point
      })
    }
  }
}
</script>

<style lang="scss">
.card-chart-container {
  width: 100%;
  display: flex;
  overflow: hidden;
  height: auto;

  h2 {
    color: $black;
  }

  flex-direction: column;
  background-color: $white;
  border-radius: 23px !important;
  box-shadow: $custom-shadow;

  @include desktop {
    padding: 1rem 2rem;
  }

  @include desktop-sm {
    padding: 1rem 2rem;
  }

  @include tablets {
    padding: 1rem 2rem;
  }

  @include mobile {
    padding: 1rem 1rem;

    h2 {
      font-size: 1.3rem;
    }
  }

  height: 100%;
  border: 0px !important;
  box-sizing: border-box;
  border-radius: 10px;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/views/selfEvaluationTests/mood/MoodTracker.vue